import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, CheckCircle2, FileText, 
  Users, AlertCircle, Trophy, Zap, 
  Clock, Phone, Mail, MapPin, DollarSign,
  ChevronRight, Truck, Receipt, CreditCard, 
  Banknote, Calculator, Share2, Eye, Send,
  Camera, Mic, Hash, Briefcase, UserPlus, 
  Building2, MessageSquare, ClipboardCheck, Shield
} from 'lucide-react';
import RequestSetupModal from '../components/ui/request-setup-modal';
import ShareModal from '../components/ui/share-modal';

// Before/After Mockup Component for Leads
const BeforeAfterMockup = () => {
  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Before - Scattered Info */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex-1 bg-slate-100 rounded-2xl p-6 border-2 border-slate-200"
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Before 2Tap...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Phone className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Scribbled on Paper</p>
                <p className="text-xs text-slate-500">Lost in the ute or washed in pocket</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <MessageSquare className="w-8 h-8 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Scattered Texts</p>
                <p className="text-xs text-slate-500">Buried in 100 other messages</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Clock className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Forgotten Follow-Ups</p>
                <p className="text-xs text-slate-500">"I'll call them back later..."</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <DollarSign className="w-8 h-8 text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Lost Opportunities</p>
                <p className="text-xs text-slate-500">Work that slipped through the cracks</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-xs font-bold text-emerald-500 text-center">2TAP<br/>CAPTURES</p>
        </motion.div>

        {/* After - 2Tap Output */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex-1 bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-6 border-2 border-emerald-200"
        >
          <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-4">2Tap creates...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <Users className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Structured Lead Record</p>
                <p className="text-xs text-emerald-600">All details in one place</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <Hash className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Reference ID</p>
                <p className="text-xs text-emerald-600">LR-1001 — easy to find</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <Briefcase className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Work Type & Value</p>
                <p className="text-xs text-emerald-600">Know what you're chasing</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <ClipboardCheck className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Ready to Follow Up</p>
                <p className="text-xs text-emerald-600">Never lose a lead again</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-4">
        <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
          <p className="text-xs font-bold text-slate-500 mb-3">BEFORE</p>
          <div className="flex items-center gap-3">
            <Phone className="w-10 h-10 text-red-500" />
            <div>
              <p className="text-sm font-semibold">Scribbled notes</p>
              <p className="text-xs text-slate-500">Lost and forgotten</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center">
          <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 border border-emerald-200">
          <p className="text-xs font-bold text-emerald-600 mb-3">AFTER</p>
          <div className="flex items-center gap-3">
            <Users className="w-10 h-10 text-emerald-500" />
            <div>
              <p className="text-sm font-semibold">Structured lead record</p>
              <p className="text-xs text-emerald-600">Always accessible</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example Lead Output Component
const ExampleLeadOutput = () => {
  const [activeTab, setActiveTab] = useState('summary');

  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      {/* Left - Input */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-slate-100 rounded-2xl p-6"
      >
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mic className="w-4 h-4" />
          YOU TELL 2TAP
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Voice note transcription:</p>
              <p className="text-sm text-slate-700 italic">
                "Got a call from Sarah at Smith Builders today. They need a renderer for a new 
                house in Kellyville. About 280 square metres external, acrylic finish. She wants 
                a quote by Friday. Job's probably worth around 25k. Got her number - 0412 345 XXX."
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Camera className="w-4 h-4 text-slate-400" />
            <span>+ Site photo attached</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <FileText className="w-4 h-4 text-slate-400" />
            <span>+ Plans PDF uploaded</span>
          </div>
        </div>
      </motion.div>

      {/* Right - Output with Tabs */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg border border-emerald-100 overflow-hidden"
      >
        {/* Tab Header */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          {[
            { id: 'summary', label: 'Lead Summary', icon: Users },
            { id: 'details', label: 'Contact Details', icon: Phone },
            { id: 'referral', label: 'Source & Referral', icon: UserPlus }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-semibold transition-colors ${
                activeTab === tab.id 
                  ? 'text-emerald-600 bg-white border-b-2 border-emerald-500' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'summary' && (
            <div className="space-y-3">
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" />
                  LEAD REFERENCE: LR-1047
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Type:</span> 
                    <span className="font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Job Lead</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Work Type:</span> 
                    <span className="font-medium">Rendering</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Client:</span> 
                    <span className="font-medium">Smith Builders</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Est. Value:</span> 
                    <span className="font-semibold text-emerald-600">$25,000</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Project Notes</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  New house in Kellyville. 280 sqm external rendering. Acrylic finish required. 
                  Quote needed by Friday.
                </p>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  FOLLOW UP
                </p>
                <p className="text-xs text-amber-800 font-medium">Quote due Friday</p>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-3">
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  CONTACT INFORMATION
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Client / Company</p>
                      <p className="text-sm font-semibold text-slate-800">Smith Builders</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Contact Person</p>
                      <p className="text-sm font-semibold text-slate-800">Sarah</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Phone</p>
                      <p className="text-sm font-semibold text-emerald-600">0412 345 XXX</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Site Location</p>
                      <p className="text-sm font-semibold text-slate-800">Kellyville, NSW</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-emerald-100 rounded-lg p-3 text-center cursor-pointer hover:bg-emerald-200 transition-colors">
                  <Phone className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-xs font-semibold text-emerald-700">Call</p>
                </div>
                <div className="bg-blue-100 rounded-lg p-3 text-center cursor-pointer hover:bg-blue-200 transition-colors">
                  <Mail className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-xs font-semibold text-blue-700">Email</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'referral' && (
            <div className="space-y-3">
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <p className="text-xs font-bold text-purple-700 mb-2 flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" />
                  SOURCE & REFERRAL TRACKING
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Lead Source</p>
                <div className="flex items-center gap-2">
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded">Existing Builder</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Entry Types Supported</p>
                <div className="space-y-2">
                  {[
                    { type: 'Job Lead', desc: 'New work opportunity', color: 'emerald' },
                    { type: 'Referral Received', desc: 'Someone sent you work', color: 'purple' },
                    { type: 'Referral Given', desc: 'You referred work out', color: 'blue' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <span className={`text-xs font-medium bg-${item.color}-100 text-${item.color}-700 px-2 py-0.5 rounded`}>{item.type}</span>
                      <span className="text-[10px] text-slate-500">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Referral Arrangements</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Flat Fee', 'Percentage', 'Reciprocal', 'Goodwill'].map((item, i) => (
                    <span key={i} className="bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-0.5 rounded">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Action */}
        <div className="p-4 pt-0">
          <div className="bg-emerald-500 rounded-lg p-3 text-center cursor-pointer hover:bg-emerald-600 transition-colors">
            <p className="text-xs font-bold text-white flex items-center justify-center gap-2">
              Save Lead & Follow Up
              <ArrowRight className="w-4 h-4" />
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Workflow Step Component
const WorkflowStep = ({ icon: Icon, label, isActive, isCompleted }) => (
  <div className={`flex flex-col items-center gap-2 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
      isActive ? 'bg-emerald-500 text-white' : 
      isCompleted ? 'bg-green-500 text-white' :
      'bg-slate-200 text-slate-500'
    }`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className={`text-xs font-medium text-center max-w-[80px] ${isActive ? 'text-emerald-600' : 'text-slate-600'}`}>
      {label}
    </p>
  </div>
);

// Main Page Component
const FeatureLeadsReferrals = () => {
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigation links for related areas
  const relatedAreas = [
    { icon: Users, label: 'Leads & Referrals', href: '/features/leads-referrals', color: 'emerald', active: true },
    { icon: Calculator, label: 'Scope & Quotes', href: '/features/scope-costing-quote', color: 'emerald' },
    { icon: ClipboardCheck, label: 'SWMS & Safety', href: '/features/swms-safety', color: 'blue' },
    { icon: Truck, label: 'Site Updates', href: '/features/site-updates', color: 'blue' },
    { icon: Receipt, label: 'Invoicing', href: '/features', color: 'orange' },
    { icon: CreditCard, label: 'Payments', href: '/features', color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation - Consistent with Landing Page */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-sm font-medium tracking-wide"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5"
            >
              <Share2 className="w-4 h-4" />
              SHARE
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]"
            >
              LOG IN
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-xs font-medium px-2"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="p-1.5 text-slate-600 hover:text-slate-900"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3"
            >
              LOG IN
            </Button>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto">
          {/* Feature Label */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider"
          >
            <Users className="w-4 h-4" />
            LEADS & REFERRALS
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Hook */}
              <p className="text-emerald-400 text-sm md:text-base font-semibold mb-3">
                Got a lead on site? Referral from a mate?
              </p>
              
              {/* Headline */}
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                CAPTURE EVERY LEAD,<br />
                <span className="text-emerald-500">BEFORE IT SLIPS AWAY</span>
              </h1>

              {/* Subheading */}
              <p className="text-slate-300 text-sm md:text-base mb-4 leading-relaxed">
                Tap, talk, done. Capture job leads and referrals with voice notes, contact details, 
                and estimated values. Never lose another opportunity.
              </p>

              {/* Supporting Line */}
              <p className="text-slate-400 text-sm border-l-2 border-emerald-500 pl-3 mb-8">
                Every lead gets a reference ID. Easy to find, easy to follow up, easy to win.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => setShowSetupModal(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm px-6 py-3 font-bold"
                >
                  REGISTER INTEREST
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Right - Visual Mockup */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden md:block"
            >
              <BeforeAfterMockup />
            </motion.div>
          </div>

          {/* Mobile Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="md:hidden mt-10"
          >
            <BeforeAfterMockup />
          </motion.div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 text-red-500 text-xs font-bold tracking-widest mb-4">
              <AlertCircle className="w-4 h-4" />
              THE PROBLEM
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              GOOD LEADS GET LOST<br />
              <span className="text-red-500">IN THE CHAOS OF THE DAY</span>
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              You're on site, phone rings, someone mentions work. You scribble a number, shove it in your pocket. 
              By the time you get home, it's gone. Or you forget. Another opportunity missed.
            </p>
          </motion.div>

          {/* Pain Points */}
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: Clock, text: 'Forgotten follow-ups' },
              { icon: Phone, text: 'Lost phone numbers' },
              { icon: DollarSign, text: 'Missed opportunities' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <item.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. What 2Tap Creates Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-emerald-500 text-xs font-bold tracking-widest mb-4">
              <CheckCircle2 className="w-4 h-4" />
              WHAT 2TAP CREATES
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              STRUCTURED LEAD RECORDS<br />
              <span className="text-emerald-500">READY TO WIN WORK</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Users, 
                title: 'Lead or Referral Log', 
                desc: 'Capture job leads, referrals received, or referrals given',
                color: 'emerald'
              },
              { 
                icon: Hash, 
                title: 'Reference ID', 
                desc: 'Auto-generated LR-1001 codes for easy tracking',
                color: 'purple'
              },
              { 
                icon: DollarSign, 
                title: 'Estimated Value', 
                desc: 'Know what each opportunity is worth',
                color: 'green'
              },
              { 
                icon: ClipboardCheck, 
                title: 'Follow-Up Ready', 
                desc: 'All details in one place to chase the work',
                color: 'blue'
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`bg-gradient-to-br from-${item.color}-50 to-white rounded-2xl p-6 border border-${item.color}-100 shadow-sm`}
              >
                <div className={`w-14 h-14 bg-${item.color}-500 rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. How It Works Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-emerald-500 text-xs font-bold tracking-widest mb-4">
              <Zap className="w-4 h-4" />
              HOW IT WORKS
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              A FEW STEPS TO<br />
              <span className="text-emerald-500">CAPTURE A LEAD</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { 
                step: '1', 
                title: 'Tap & Talk', 
                desc: 'Open 2Tap and hit the mic. Speak the details — name, number, what they need, when they need it.' 
              },
              { 
                step: '2', 
                title: '2Tap Structures It', 
                desc: 'AI extracts the key info: contact details, work type, estimated value, timing. Ready to review.' 
              },
              { 
                step: '3', 
                title: 'Save & Follow Up', 
                desc: 'Confirm the details and save. Your lead is logged with a reference ID. Chase it when you\'re ready.' 
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-black text-xl">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Example Output Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-slate-500 text-xs font-bold tracking-widest mb-4">
              <FileText className="w-4 h-4" />
              EXAMPLE
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              SEE THE<br />
              <span className="text-emerald-500">OUTPUT</span>
            </h2>
          </motion.div>

          <ExampleLeadOutput />
        </div>
      </section>

      {/* 5.5 Lead Types Feature Highlight */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-purple-500 text-xs font-bold tracking-widest mb-4">
              <UserPlus className="w-4 h-4" />
              LEAD TYPES
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              TRACK LEADS<br />
              <span className="text-purple-500">AND REFERRALS</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                type: 'Job Lead',
                icon: Briefcase,
                desc: 'New work opportunity from a builder, client, or direct enquiry. Capture the details and follow up to win the job.',
                color: 'emerald',
                examples: ['Builder tender invite', 'Direct client call', 'Site visit enquiry']
              },
              { 
                type: 'Referral Received',
                icon: UserPlus,
                desc: 'Someone sent work your way. Track who referred it, any fee arrangements, and don\'t forget to say thanks.',
                color: 'purple',
                examples: ['Mate passed on a job', 'Builder recommendation', 'Partner introducer']
              },
              { 
                type: 'Referral Given',
                icon: Send,
                desc: 'You referred work to someone else. Track reciprocal arrangements and keep relationships strong.',
                color: 'blue',
                examples: ['Passed to subbie', 'Recommended a mate', 'Out of scope job']
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
              >
                <div className={`w-14 h-14 bg-${item.color}-500 rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.type}</h3>
                <p className="text-sm text-slate-600 mb-4">{item.desc}</p>
                <div className="space-y-1.5">
                  {item.examples.map((ex, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs text-slate-500">
                      <CheckCircle2 className={`w-3.5 h-3.5 text-${item.color}-500`} />
                      {ex}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Why It Matters Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHY IT MATTERS
            </h2>
            <p className="text-emerald-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Every lead you capture is potential revenue. Every one you lose is money walking away.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Trophy, title: 'Win More Work', desc: 'Follow up on every opportunity' },
              { icon: Clock, title: 'Save Time', desc: 'Capture leads in seconds' },
              { icon: DollarSign, title: 'Track Value', desc: 'Know your pipeline worth' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-5"
              >
                <item.icon className="w-10 h-10 text-white mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-emerald-100">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Where It Fits Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
              <Trophy className="w-4 h-4" />
              PART OF WIN THE WORK
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHERE IT FITS IN<br />
              <span className="text-emerald-500">2TAP</span>
            </h2>
          </motion.div>

          {/* Workflow */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 shadow-sm mb-10">
            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
              <WorkflowStep icon={Users} label="Lead / Referral" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Calculator} label="Scope & Quote" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={FileText} label="Contract" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Trophy} label="Job Won" />
            </div>
          </div>

          {/* Related Areas */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {relatedAreas.map((item, i) => (
              <div 
                key={i} 
                onClick={() => navigate(item.href)}
                className={`flex items-center gap-2 rounded-lg p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                  item.active 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <item.icon className={`w-5 h-5 ${item.active ? 'text-white' : `text-${item.color}-500`}`} />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Final CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              READY TO STOP<br />
              <span className="text-emerald-500">LOSING LEADS?</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Every lead you miss is money in someone else's pocket. Set up your business with 2Tap and start capturing opportunities today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => setShowSetupModal(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm px-8 py-4 font-bold text-lg"
              >
                REGISTER INTEREST
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 2Tap. Powered for Smart Business.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Setup Modal */}
      <RequestSetupModal 
        isOpen={showSetupModal} 
        onClose={() => setShowSetupModal(false)} 
      />

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
      />
    </div>
  );
};

export default FeatureLeadsReferrals;
